// client/src/pages/ShopPOS.tsx
import { useState, useEffect, useRef } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import {
  getCurrentShop,
  getCurrentUser,
  getCurrentUserPermissions,
} from "@/lib/auth";
import {
  createSale,
  getShopSales,
  getShopBankCards,
  getCustomerByPhoneAndShop,
} from "@/lib/api";
import { useShift } from "@/lib/hooks/use-shift";
import { useOfflineContext } from "@/lib/contexts/offline-context";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ShoppingCart, AlertCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { usePlan } from "@/hooks/usePlan";
import { indexedDBService } from "@/lib/db/indexed-db";
import { syncManager } from "@/lib/services/sync-manager";

// Hooks modularizados
import { usePOSCart, usePOSInventory, usePOSOffline } from "./ShopPOS/hooks";

// Componentes modularizados
import {
  CartSidebar,
  ProductGrid,
  OfflineIndicator,
  PaymentModal,
  TransferFormModal,
  CashInputModal,
  SplitPaymentModal,
  OpenShiftModal,
  CloseTypeModal,
  DenominationModal,
} from "./ShopPOS/components";

// Utilidades
import { getCurrencySymbol, formatMoney } from "./ShopPOS/utils/currency";
import {
  DEFAULT_DENOMINATIONS,
  calculateTotalFromDenominations,
} from "./ShopPOS/utils/denominations";

// Tipos
import {
  TransferData,
  DEFAULT_TRANSFER_DATA,
  CloseType,
  PaymentMethod,
} from "./ShopPOS/types";

export default function ShopPOS() {
  const currentShop = getCurrentShop();
  const currentUser = getCurrentUser();
  const userName = currentUser?.fullName || currentUser?.username || "Usuario";
  const queryClient = useQueryClient();

  // ============ PERMISOS Y PLAN ============
  const { isOwnerInShopMode: isOwnerMode } = getCurrentUserPermissions();
  const ownerInShopMode = isOwnerMode;
  const { planType } = usePlan();
  const canUseShifts = planType === "professional" || planType === "enterprise";

  // ============ USUARIO REAL ============
  const [realUser, setRealUser] = useState<any>(null);
  const [realUserLoaded, setRealUserLoaded] = useState(false);

  useEffect(() => {
    if (ownerInShopMode && currentShop) {
      const virtualUser = {
        id: `virtual_owner_${currentShop.id}`,
        shopId: currentShop.id,
        fullName: "Dueño de la Tienda",
        username: `owner_${currentShop.id.substring(0, 4)}`,
        role: "supervisor",
        email: null,
        isVirtual: true,
      };
      setRealUser(virtualUser);
      setRealUserLoaded(true);
    } else {
      const user = getCurrentUser();
      if (user) {
        setRealUser(user);
        setRealUserLoaded(true);
      }
    }
  }, [currentShop?.id, ownerInShopMode]);

  // ============ ESCUCHAR CIERRE DE TURNO SINCRONIZADO ============
  useEffect(() => {
    const unsub = syncManager.on((event) => {
      if (event.type === "sync:shift-closed" && event.data?.shiftId) {
        const shiftId = event.data.shiftId;
        const pdfUrl = `/api/shifts/${shiftId}/pdf`;
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = `acta_cierre_${shiftId.substring(0, 8)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
    return unsub;
  }, []);

  // ============ HOOK DE TURNOS ============
  // Obtener estado de conectividad (debe ir antes de useShift)
  const { isOfflineMode } = useOfflineContext();

  const {
    currentShift,
    cashRegisters,
    shiftLoading,
    showOpenShiftModal,
    setShowOpenShiftModal,
    openShift,
    closeShift,
    refetchShift,
    isOpeningShift,
    isClosingShift,
  } = useShift(realUser?.id, isOfflineMode);

  // ============ ESTADOS LOCALES ============
  const [customerPhone, setCustomerPhone] = useState("");
  const [customer, setCustomer] = useState<any>(null);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Estados de modales de pago
  const [showPaymentMethod, setShowPaymentMethod] = useState(false);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [showCashInput, setShowCashInput] = useState(false);
  const [transferData, setTransferData] = useState<TransferData>(
    DEFAULT_TRANSFER_DATA,
  );
  const [cashReceived, setCashReceived] = useState("");

  // {esta sección agrega estado para el modal de pago dividido}
  const [showSplitPayment, setShowSplitPayment] = useState(false);

  // Estados de cierre de turno
  const [showCloseTypeModal, setShowCloseTypeModal] = useState(false);
  const [selectedCloseType, setSelectedCloseType] = useState<CloseType>(null);
  const [showDenominationModal, setShowDenominationModal] = useState(false);
  const [denominations, setDenominations] = useState<Record<string, number>>(
    DEFAULT_DENOMINATIONS,
  );
  const [tempClosingNotes, setTempClosingNotes] = useState("");
  const [shiftSummary, setShiftSummary] = useState<any>(null);
  const [hasBeenCounted, setHasBeenCounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Estados de autorización
  const [showAuthorizationField, setShowAuthorizationField] = useState(false);
  const [pendingAuthShift, setPendingAuthShift] = useState<any>(null);
  const [authorizationCode, setAuthorizationCode] = useState("");
  const [isAuthorizing, setIsAuthorizing] = useState(false);

  // Ref para evitar re-verificación del backend después de autorización local
  const lastLocalAuthRef = useRef<number>(0);

  // ============ ESTADO PARA TARJETAS BANCARIAS ============
  const [shopBankCards, setShopBankCards] = useState<any[]>([]);
  // ============ FIN ESTADO ============

  // ============ HOOK DE CARRITO (PRIMERO - necesita fullInventory pero aún no existe) ============
  // {TEMPORAL: Usar array vacío inicialmente, se actualizará cuando fullInventory esté listo}

  // ============ HOOK DE INVENTARIO (SEGUNDO) ============
  const {
    search,
    setSearch,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    currentPage,
    setCurrentPage,
    totalPages,
    totalItems,
    filteredInventory,
    displayCartItems,
    fullInventory,
    products,
    inventoryData,
    inventoryLoading,
    isLoading,
    refreshInventory,
    clearFilters,
    saveInventoryForOffline,
  } = usePOSInventory({ currentShop });

  // ============ HOOK DE CARRITO (TERCERO - ahora con fullInventory real) ============
  const {
    cart,
    setCart,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateSurcharge,
    clearCart,
    cartTotal,
    cartTotalWithSurcharge,
  } = usePOSCart(fullInventory, getCurrencySymbol);

  // ============ HOOK OFFLINE (CUARTO) ============
  const {
    pendingSalesCount,
    isSyncing,
    syncProgress,
    pendingAuthShift: offlinePendingShift,
    processOfflineSale,
    syncPendingSales,
    openOfflineShift,
    closeOfflineShift,
    clearPendingAuthShift,
    authorizeLocalClose,
  } = usePOSOffline({
    currentShop,
    currentShift,
    cart: cart,
    displayCartItems: displayCartItems,
    cartTotal: cartTotal,
    cartTotalWithSurcharge: cartTotalWithSurcharge,
    inventoryData: inventoryData,
    realUserId: realUser?.id,
  });

  // {NUEVO SECCIÓN: Sincronizar estado pendiente del hook offline con el estado local}
  // Esta sección se encarga de escuchar cuando el hook offline detecta un turno pendiente
  // durante la sincronización y actualiza automáticamente la UI para mostrar el modal
  useEffect(() => {
    if (offlinePendingShift) {
      console.log(
        "🔐 [POS] Hook offline detectó turno pendiente:",
        offlinePendingShift,
      );

      // {Actualizar el estado local para mostrar el modal}
      setPendingAuthShift({
        id: offlinePendingShift.shiftId,
        isLocal: offlinePendingShift.isLocal,
      });
      setShowAuthorizationField(true);

      // {Cargar los datos del turno pendiente en los estados del modal}
      if (offlinePendingShift.denominations) {
        setDenominations(offlinePendingShift.denominations);
      }
      if (offlinePendingShift.notes) {
        setTempClosingNotes(offlinePendingShift.notes);
      }

      // {Cerrar modal de apertura si está abierto (turno local pendiente de autorización)}
      if (offlinePendingShift.isLocal) {
        setShowOpenShiftModal(false);
      }

      // {Mostrar el modal de desglose automáticamente}
      setShowDenominationModal(true);

      // {Notificar al usuario}
      toast({
        title: "⚠️ Turno pendiente de autorización",
        description:
          "Se requiere código de supervisor para completar el cierre",
        variant: "default",
        duration: 6000,
      });
    }
  }, [offlinePendingShift]);

  // ============ CARGAR TARJETAS BANCARIAS AL MONTAR ============
  useEffect(() => {
    if (currentShop?.id) {
      const loadCards = async () => {
        try {
          const cards = await getShopBankCards(currentShop.id);
          setShopBankCards(cards);
        } catch (error) {
          console.warn("No se pudieron cargar las tarjetas bancarias:", error);
        }
      };
      loadCards();
    }
  }, [currentShop?.id]);
  // ============ FIN CARGA ============

  // {SECCIÓN MEJORADA: Verificar turno pendiente al cargar la página}  // {SECCIÓN MEJORADA: Verificar turno pendiente al cargar la página}
  // Primero intenta recuperar de localStorage (rápido), luego consulta al backend (confiable)
  useEffect(() => {
    const checkPendingShift = async () => {
      if (showDenominationModal) return; // No abrir otro modal si ya hay uno

      // {CORRECCIÓN: Evitar re-verificación justo después de una autorización local}
      if (lastLocalAuthRef.current > 0 && Date.now() - lastLocalAuthRef.current < 5000) {
        console.log("⏳ [POS] Ignorando verificación post-autorización local");
        return;
      }

      // 1. Intentar desde localStorage primero (respuesta inmediata)
      const pendingData = localStorage.getItem("pendingAuthShift");
      if (pendingData) {
        try {
          const pending = JSON.parse(pendingData);
          console.log(
            "🔐 [POS] Turno pendiente encontrado en localStorage:",
            pending,
          );

          setPendingAuthShift({ id: pending.shiftId });
          setShowAuthorizationField(true);
          setDenominations(pending.denominations || DEFAULT_DENOMINATIONS);
          setTempClosingNotes(pending.notes || "");
          setShowDenominationModal(true);

          toast({
            title: "⚠️ Autorización requerida",
            description:
              "El cierre del turno anterior requiere autorización de supervisor",
            variant: "default",
          });
          return; // Ya lo encontramos, no seguir
        } catch (e) {
          console.error(
            "Error recuperando turno pendiente de localStorage:",
            e,
          );
          localStorage.removeItem("pendingAuthShift"); // Limpiar datos corruptos
        }
      }

      // 2. Si no hay en localStorage, consultar al backend
      if (currentShop?.id) {
        try {
          const response = await fetch(
            `/api/shops/${currentShop.id}/pending-authorization`,
          );
          const data = await response.json();

          if (data.success && data.hasPendingAuth && data.shift) {
            console.log(
              "🔐 [POS] Turno pendiente encontrado en backend:",
              data.shift,
            );

            setPendingAuthShift({ id: data.shift.id });
            setShowAuthorizationField(true);

            // Cargar denominaciones si existen
            if (data.shift.denominationBreakdown) {
              setDenominations(data.shift.denominationBreakdown);
            }
            if (data.shift.notes) {
              setTempClosingNotes(data.shift.notes);
            }

            setShowDenominationModal(true);

            // Guardar en localStorage para futuras recargas
            localStorage.setItem(
              "pendingAuthShift",
              JSON.stringify({
                shiftId: data.shift.id,
                denominations:
                  data.shift.denominationBreakdown || DEFAULT_DENOMINATIONS,
                notes: data.shift.notes || "",
              }),
            );

            toast({
              title: "⚠️ Autorización requerida",
              description:
                "El cierre del turno anterior requiere autorización de supervisor",
              variant: "default",
            });
          }
        } catch (error) {
          console.error("Error consultando turno pendiente al backend:", error);
        }
      }
    };

    checkPendingShift();
  }, [currentShop?.id, showDenominationModal]);

  // Guardar inventario para offline cuando está online
  useEffect(() => {
    if (fullInventory.length > 0 && !isOfflineMode) {
      saveInventoryForOffline();
    }
  }, [fullInventory, isOfflineMode]);

  // {NUEVO: Mostrar modal de apertura automáticamente si no hay turno}
  useEffect(() => {
    // {CORRECCIÓN: No mostrar si hay turno offline pendiente de autorización}
    if (offlinePendingShift) {
      return;
    }
    // No mostrar modal mientras offline — esperar sincronización
    if (isOfflineMode) {
      return;
    }
    if (realUserLoaded && !currentShift && !shiftLoading && canUseShifts) {
      const timer = setTimeout(() => {
        setShowOpenShiftModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [realUserLoaded, currentShift, shiftLoading, canUseShifts, offlinePendingShift, isOfflineMode]);

  // {NUEVO: Resetear isClosing cuando el turno desaparece tras cierre offline}
  useEffect(() => {
    if (!currentShift && !shiftLoading) {
      setIsClosing(false);
    }
  }, [currentShift, shiftLoading]);

  // ============ MUTATION DE VENTA ============
  const createSaleMutation = useMutation({
    mutationFn: createSale,
    onSuccess: (saleData) => {
      const displayTotal =
        cartTotalWithSurcharge > cartTotal ? cartTotalWithSurcharge : cartTotal;
      toast({
        title: "Venta Completada",
        description: `Total cobrado: ${formatMoney(displayTotal)}`,
      });

      setCart([]);
      setCustomer(null);
      setCustomerPhone("");
      setTransferData(DEFAULT_TRANSFER_DATA);
      setCashReceived("");
      setIsProcessing(false);
      setIsCartOpen(false);

      refreshInventory();
      queryClient.invalidateQueries({
        queryKey: ["shopSales", currentShop?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["shopCustomers", currentShop?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["currentShift", currentShop?.id],
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo completar la venta",
        variant: "destructive",
      });
      setIsProcessing(false);
    },
  });

  // ============ FUNCIONES DE CLIENTE ============
  const handleCustomerSearch = async () => {
    if (!customerPhone.trim()) {
      toast({
        title: "Error",
        description: "Ingresa un número de teléfono",
        variant: "destructive",
      });
      return;
    }
    setCustomerSearching(true);
    try {
      const cleanPhone = customerPhone.replace(/[\s\-\(\)]/g, "");
      const foundCustomer = await getCustomerByPhoneAndShop(
        currentShop!.id,
        cleanPhone,
      );
      setCustomer(foundCustomer);
      toast({
        title: "Cliente encontrado",
        description: `Cliente: ${foundCustomer.fullName}`,
      });
    } catch {
      toast({
        title: "Cliente no encontrado",
        description: "No se encontró el cliente",
      });
      setCustomer(null);
    } finally {
      setCustomerSearching(false);
    }
  };

  const clearCustomer = () => {
    setCustomer(null);
    setCustomerPhone("");
  };

  // ============ FUNCIONES DE PAGO ============
  const handleCheckout = () => {
    if (canUseShifts && !currentShift) {
      toast({
        title: "Turno no activo",
        description: "Debes abrir un turno",
        variant: "destructive",
      });
      setShowOpenShiftModal(true);
      return;
    }
    if (cartTotal === 0) {
      toast({
        title: "Carrito vacío",
        description: "Agrega productos",
        variant: "destructive",
      });
      return;
    }
    setShowPaymentMethod(true);
  };

  // {esta sección maneja la selección del método de pago, incluyendo el nuevo flujo mixto}
  const confirmPayment = (method: PaymentMethod) => {
    if (method === "efectivo") {
      setShowCashInput(true);
      setShowPaymentMethod(false);
    } else if (method === "mixto") {
      // {esta sección abre el modal de pago dividido para método mixto}
      setShowSplitPayment(true);
      setShowPaymentMethod(false);
    } else {
      // Para transferencia, mostrar el formulario primero
      setShowPaymentMethod(false);
      setShowTransferForm(true);
    }
  };

  const handleTransferConfirm = () => {
    // Sin validaciones - todos los campos son opcionales
    setShowTransferForm(false);
    processSale("transferencia");
  };

  // {esta sección recibe montos + datos del cliente, actualiza el estado de transferencia y procesa la venta}
  const handleSplitPaymentConfirm = (
    cashAmount: number,
    transferAmount: number,
    customerData: {
      fullName?: string;
      ci?: string;
      phone?: string;
      transferCode?: string;
      bank?: string;
      destinationCard?: string;
      firstFourDigits?: string;
      lastFourDigits?: string;
    },
  ) => {
    setShowSplitPayment(false);

    setTransferData({
      ...transferData,
      fullName: customerData.fullName || "",
      ci: customerData.ci || "",
      phone: customerData.phone || "",
      transferCode: customerData.transferCode || "",
      bank: customerData.bank || "",
      destinationCard: customerData.destinationCard || "",
      firstFourDigits: customerData.firstFourDigits || "",
      lastFourDigits: customerData.lastFourDigits || "",
    });

    // ✅ Pasar transferAmount como TERCER argumento
    console.log("🔍 [DEBUG] handleSplitPaymentConfirm llama a processSale:", {
      method: "mixto",
      cashAmount,
      transferAmount,
    });

    processSale("mixto", cashAmount, transferAmount);
  };

  // {esta sección actualiza processSale para aceptar monto de transferencia en pagos mixtos}
  const processSale = async (
    method: PaymentMethod,
    cashReceivedAmount: number = 0,
    transferAmount?: number, // ← NUEVO: Monto por transferencia en pagos mixtos
  ) => {
    // {🔍 LOG: Verificar parámetros en processSale}
    if (method === "mixto") {
    }
    setIsCartOpen(false);
    setShowPaymentMethod(false);
    setShowTransferForm(false);
    setShowCashInput(false);
    setIsProcessing(true);

    try {
      if (isOfflineMode) {
        await processOfflineSale(
          method,
          cashReceivedAmount,
          cart,
          customer,
          transferData,
          method === "mixto" ? transferAmount : undefined,
        );
        setCart([]);
        setCustomer(null);
        setCustomerPhone("");
        setTransferData(DEFAULT_TRANSFER_DATA);
        setCashReceived("");
        toast({
          title: "📴 Venta guardada localmente",
          description: `Total: ${formatMoney(method === "transferencia" ? cartTotalWithSurcharge : cartTotal)}`,
        });
        setIsProcessing(false);
      } else {
        // {CORRECCIÓN: Validar que exista un turno activo antes de crear venta online}
        if (!currentShift?.id) {
          toast({
            title: "❌ Error de sincronización",
            description:
              "No se detectó el turno activo. Recarga la página e intenta de nuevo.",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }

        // {SALE GATE: Si el turno aún es offline, sincronizar antes de vender online}
        let activeShiftId = currentShift.id;
        if (activeShiftId.startsWith("offline_shift_")) {
          console.log("🔄 [SALE-GATE] Turno offline detectado, sincronizando antes de vender...");
          toast({
            title: "🔄 Sincronizando turno",
            description: "Espera mientras se sincroniza el turno para procesar la venta...",
          });
          try {
            await syncPendingSales();
            await queryClient.refetchQueries({ queryKey: ["currentShift"] });
            const freshShift: any = queryClient.getQueryData(["currentShift", currentShop?.id])
              || queryClient.getQueryData(["currentShift", realUser?.id]);
            if (freshShift?.id && !freshShift.id.startsWith("offline_shift_")) {
              activeShiftId = freshShift.id;
              queryClient.setQueryData(["currentShift", currentShop?.id], freshShift);
              console.log("✅ [SALE-GATE] Turno sincronizado, nuevo ID:", activeShiftId);
            } else {
              throw new Error("No se pudo obtener el ID real del turno tras sincronizar");
            }
          } catch (err) {
            toast({
              title: "❌ Error de sincronización",
              description: "No se pudo sincronizar el turno. Intenta de nuevo.",
              variant: "destructive",
            });
            setIsProcessing(false);
            return;
          }
        }

        const saleItems = cart.map((cartItem) => {
          const invItem = fullInventory.find(
            (i: any) => i.id === cartItem.inventoryId,
          );

          if (!invItem) {
            console.error(
              `❌ Producto no encontrado en fullInventory: ${cartItem.inventoryId}`,
            );
            throw new Error(`Producto no encontrado`);
          }

          const basePrice = parseFloat(invItem.price);
          const surchargePercentage = cartItem.surchargePercentage || 0;
          const finalPrice = basePrice * (1 + surchargePercentage / 100);

          return {
            productId: invItem.productId,
            quantity: cartItem.quantity,
            price: finalPrice,
            surchargePercentage,
          };
        });

        // {LOG DE DEBUG: Verificar qué shiftId se envía realmente}
        console.log("🧾 [VENTA-ONLINE] Payload de venta:", {
          shiftId: activeShiftId,
          total: cartTotal.toFixed(2),
          paymentMethod: method,
        });

        const payload: any = {
          shopId: currentShop!.id,
          total: (method === "transferencia"
            ? cartTotalWithSurcharge
            : cartTotal
          ).toFixed(2),
          items: saleItems,
          paymentMethod: method,
          shiftId: activeShiftId,
        };

        if (customer) payload.customerId = customer.id;
        if (method === "efectivo" && cashReceivedAmount > 0) {
          payload.cashReceived = cashReceivedAmount.toFixed(2);
          payload.changeGiven = (cashReceivedAmount - cartTotal).toFixed(2);
        }
        if (method === "transferencia") {
          payload.fullName = transferData.fullName?.trim();
          payload.ci = transferData.ci?.trim();
          payload.phone = transferData.phone?.trim();
          payload.transferCode = transferData.transferCode?.trim();
          payload.bank = transferData.bank;

          // ============ NUEVO: Enviar tarjeta de destino ============
          if (transferData.destinationCard) {
            payload.destinationCard = transferData.destinationCard;
          }
          // ============ FIN NUEVO CAMPO ============

          if (transferData.firstFourDigits?.trim()) {
            payload.firstFourDigits = transferData.firstFourDigits.trim();
          }
          if (transferData.lastFourDigits?.trim()) {
            payload.lastFourDigits = transferData.lastFourDigits.trim();
          }
        }

        // {esta sección agrega los campos para pago mixto: cashAmount + transferAmount}
        if (method === "mixto") {
          if (
            cashReceivedAmount === undefined ||
            transferAmount === undefined
          ) {
            throw new Error("Error interno: faltan montos para pago mixto");
          }

          payload.cashAmount = cashReceivedAmount.toFixed(2);
          payload.transferAmount = transferAmount.toFixed(2);
          payload.cashReceived = cashReceivedAmount.toFixed(2);
          payload.changeGiven = (cashReceivedAmount - cartTotal).toFixed(2);

          // Datos de transferencia para trazabilidad completa
          payload.fullName = transferData.fullName?.trim() || undefined;
          payload.ci = transferData.ci?.trim() || undefined;
          payload.phone = transferData.phone?.trim() || undefined;
          payload.transferCode = transferData.transferCode?.trim() || undefined;
          payload.bank = transferData.bank || undefined;
          if (transferData.destinationCard) {
            payload.destinationCard = transferData.destinationCard;
          }
          if (transferData.firstFourDigits?.trim()) {
            payload.firstFourDigits = transferData.firstFourDigits.trim();
          }
          if (transferData.lastFourDigits?.trim()) {
            payload.lastFourDigits = transferData.lastFourDigits.trim();
          }
        }

        // {esta sección muestra el payload FINAL que se enviará al backend}
        console.log("🚀 [PAYLOAD FINAL] Enviando al backend:", {
          paymentMethod: payload.paymentMethod,
          total: payload.total,
          cashAmount: payload.cashAmount,
          transferAmount: payload.transferAmount,
          cashReceived: payload.cashReceived,
          changeGiven: payload.changeGiven,
          shiftId: payload.shiftId,
          itemsCount: payload.items?.length,
        });

        // {Persistir venta localmente para cierre offline (write-through cache)}
        try {
          await indexedDBService.saveSale({
            id: `online_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            shopId: payload.shopId,
            total: parseFloat(payload.total),
            paymentMethod: payload.paymentMethod,
            shiftId: payload.shiftId,
            items: payload.items || [],
            createdAt: new Date().toISOString(),
            cashReceived: payload.cashReceived ? parseFloat(payload.cashReceived) : undefined,
            changeGiven: payload.changeGiven ? parseFloat(payload.changeGiven) : undefined,
            cashAmount: payload.cashAmount ? parseFloat(payload.cashAmount) : undefined,
            transferAmount: payload.transferAmount ? parseFloat(payload.transferAmount) : undefined,
            synced: 1,
          });
        } catch (localErr) {
          console.warn("⚠️ No se pudo guardar venta online localmente:", localErr);
        }

        createSaleMutation.mutate(payload);
      }
    } catch (error) {
      console.error("Error procesando venta:", error);
      toast({
        title: "Error",
        description: "No se pudo procesar la venta",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  // ============ FUNCIONES DE TURNO ============
  const handleOpenShift = async (
    cashRegisterId: string,
    initialCash: string,
  ) => {
    // {NUEVO: Validar monto inicial}
    const amount = parseFloat(initialCash);
    if (isNaN(amount) || amount < 0) {
      toast({
        title: "❌ Error",
        description: "El monto inicial debe ser un número positivo o cero",
        variant: "destructive",
      });
      return;
    }
    try {
      if (isOfflineMode) {
        // Modo OFFLINE
        const offlineShift = await openOfflineShift(
          cashRegisterId,
          initialCash,
        );

        // Actualizar estado local
        queryClient.setQueryData(["currentShift", realUser?.id], offlineShift);

        setShowOpenShiftModal(false);
        toast({
          title: "📴 Turno offline abierto",
          description: "Se sincronizará cuando vuelva la conexión",
        });
      } else {
        // Modo ONLINE
        await openShift(cashRegisterId, initialCash);
        await refetchShift();

        // {NUEVO: Guardar employeeId real para uso offline}
        if (realUser?.id) {
          localStorage.setItem("lastEmployeeId", realUser.id);
          console.log(
            "💾 [POS] EmployeeId guardado para offline:",
            realUser.id,
          );
        }

        setShowOpenShiftModal(false);
        toast({
          title: "✅ Turno abierto",
          description: "Turno iniciado exitosamente",
        });
      }
    } catch (error: any) {
      toast({
        title: "❌ Error",
        description: error.message || "No se pudo abrir el turno",
        variant: "destructive",
      });
    }
  };

  const prepareCloseShift = async () => {
    if (!currentShift) return;

    // Detectar si es turno offline
    const isOfflineShift = currentShift.id.startsWith("offline_shift_");

    if (isOfflineShift) {
      // Obtener datos del turno offline de IndexedDB
      const offlineShift = await indexedDBService.getOfflineShiftById(currentShift.id);
      if (offlineShift) {
        setShiftSummary({
          initialCash: parseFloat(offlineShift.initialCash) || 0,
          cashSales: parseFloat(offlineShift.cashSales) || 0,
          transferSales: parseFloat(offlineShift.transferSales) || 0,
          cardSales: parseFloat(offlineShift.cardSales) || 0,
          totalSales: parseFloat(offlineShift.totalSales) || 0,
          expectedCash: 0, // Caja ciega
          openedAt: offlineShift.openedAt,
          openedBy: offlineShift.openedBy,
        });
        setHasBeenCounted(false);
        setShowCloseTypeModal(true);
        return;
      }
    }

    // Obtener resumen del turno para mostrar en el modal
    try {
      const response = await fetch(
        `/api/shifts/${currentShift.id}/summary-blind`,
      );
      const data = await response.json();

      if (data.success) {
        setShiftSummary({
          initialCash: data.summary.initialCash,
          cashSales: data.summary.cashSales,
          transferSales: data.summary.transferSales,
          cardSales: data.summary.cardSales,
          totalSales: data.summary.totalSales,
          expectedCash: 0, // Caja ciega
          openedAt: currentShift.openedAt,
          openedBy: currentShift.openedBy,
        });
        setHasBeenCounted(data.summary.hasBeenCounted);
      }
    } catch (error) {
      console.error("Error obteniendo resumen del turno:", error);
      toast({
        title: "Error",
        description: "No se pudo obtener el resumen",
        variant: "destructive",
      });
      return;
    }

    setShowCloseTypeModal(true);
  };

  const handleSelectCloseType = async () => {
    setSelectedCloseType("day_close");
    setShowCloseTypeModal(false);

    // Resetear denominaciones
    setDenominations(DEFAULT_DENOMINATIONS);
    setTempClosingNotes("");
    setAuthorizationCode("");
    setShowAuthorizationField(false);

    // Mostrar modal de desglose
    setShowDenominationModal(true);
  };

  const handleCloseShift = async () => {
    if (isClosing) return;

    const calculatedTotal = calculateTotalFromDenominations(denominations);

    // Validación de efectivo
    const cashSales = shiftSummary?.cashSales || 0;
    if (calculatedTotal === 0 && cashSales > 0) {
      toast({
        title: "⚠️ Aviso",
        description:
          "Hay ventas en efectivo. Verifica que el desglose sea correcto.",
      });
    }

    if (calculatedTotal === 0 && cashSales === 0) {
      console.log(
        "✅ No hubo ventas en efectivo - Cerrando turno con desglose $0",
      );
    }

    setIsClosing(true);

    try {
      // {NUEVO: MODO OFFLINE}
      if (isOfflineMode) {
        // {NUEVO: Calcular el esperado (initialCash + cashSales)}
        const expectedCashAmount =
          (shiftSummary?.initialCash || 0) + (shiftSummary?.cashSales || 0);

        const result = await closeOfflineShift(
          currentShift.id,
          calculatedTotal.toString(),
          tempClosingNotes,
          denominations,
          selectedCloseType || "day_close",
          expectedCashAmount.toString(), // ← NUEVO: Pasar el esperado
        );

        // {CORRECCIÓN: Toast genérico SIN mostrar el monto de la diferencia}
        if (result.hasDiscrepancy) {
          toast({
            title: "⚠️ Discrepancia en el arqueo",
            description:
              "El cierre quedará pendiente de autorización del supervisor.",
            variant: "default", // No usar destructive para no alarmar
          });
        } else {
          toast({
            title: "📴 Turno cerrado",
            description: "El cierre se sincronizará cuando vuelva la conexión",
          });
        }

        // Limpiar localStorage
        localStorage.removeItem("pendingAuthShift");

        // Limpiar estados
        setShowDenominationModal(false);
        setPendingAuthShift(null);
        setShiftSummary(null);
        setAuthorizationCode("");
        setSelectedCloseType(null);

        // Refrescar turno
        await refetchShift();

        // Mantener isClosing=true hasta que sync complete y el turno desaparezca
        return;
      }

      // {MODO ONLINE - CÓDIGO EXISTENTE}
      // {Proteger contra currentShift indefinido}
      if (!currentShift?.id) {
        toast({
          title: "Error",
          description: "No hay turno activo para cerrar",
          variant: "destructive",
        });
        setIsClosing(false);
        return;
      }

      // {CLOSE GATE: Sincronizar operaciones pendientes antes del cierre online}
      if (pendingSalesCount > 0) {
        console.log(`🔒 [CLOSE-GATE] ${pendingSalesCount} ops pendientes, sincronizando antes del cierre...`);
        toast({
          title: "🔄 Sincronizando",
          description: `${pendingSalesCount} operaciones pendientes — sincronizando antes del cierre...`,
        });
        try {
          await syncPendingSales();
          await refetchShift();
          console.log("✅ [CLOSE-GATE] Sync completado, procediendo con cierre online");
        } catch (syncErr) {
          console.warn("⚠️ [CLOSE-GATE] Error en sync, continuando con cierre:", syncErr);
          toast({
            title: "⚠️ Sincronización parcial",
            description: "No se pudieron sincronizar todas las operaciones. El cierre continuará.",
          });
        }
      }

      const result = await closeShift(
        currentShift.id,
        calculatedTotal.toString(),
        tempClosingNotes,
        denominations,
        authorizationCode,
      );

      toast({
        title: "✅ Turno cerrado",
        description: "El turno se ha cerrado exitosamente",
      });

      // Limpiar localStorage
      localStorage.removeItem("pendingAuthShift");

      // {NUEVO: Limpiar también el estado en el hook offline}
      clearPendingAuthShift();

      // Limpiar estados
      setShowDenominationModal(false);
      setPendingAuthShift(null);
      setShiftSummary(null);
      setAuthorizationCode("");

      // Descargar PDF para AMBOS tipos de cierre
      const pdfUrl = `/api/shifts/${currentShift.id}/pdf`;
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `acta_cierre_${currentShift.id.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Si es cierre del día, validar automáticamente
      if (selectedCloseType === "day_close") {
        try {
          const userId = currentUser?.id || realUser?.id || "sistema";
          await fetch(`/api/shifts/${currentShift.id}/validate`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-user-id": userId,
            },
            body: JSON.stringify({
              countedCash:
                calculateTotalFromDenominations(denominations).toString(),
              validatedBy: userId,
              isAutoValidation: true,
            }),
          });
          console.log("✅ Turno validado automáticamente (cierre de jornada)");
        } catch (e) {
          console.error("❌ Error en validación automática:", e);
        }
      }

      setSelectedCloseType(null);

      // Refrescar turno
      await refetchShift();
    } catch (error: any) {
      console.error("Error cerrando turno:", error);
      console.log("🔍 [DEBUG] error.message:", error.message);
      console.log(
        "🔍 [DEBUG] ¿contiene 'código de supervisor'?:",
        error.message?.includes("código de supervisor"),
      );

      if (error.message?.includes("código de supervisor")) {
        // Guardar el turno como pendiente de autorización
        setPendingAuthShift(currentShift);
        setShowAuthorizationField(true);

        // Guardar en localStorage para persistir al recargar
        const pendingData = {
          shiftId: currentShift.id,
          denominations,
          notes: tempClosingNotes,
          closeType: selectedCloseType,
        };
        localStorage.setItem("pendingAuthShift", JSON.stringify(pendingData));

        // {NUEVO: También guardar en BD para respaldo confiable}
        try {
          await fetch(`/api/shifts/${currentShift.id}/pending-authorization`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              countedCash:
                calculateTotalFromDenominations(denominations).toString(),
              notes: tempClosingNotes,
              denominationBreakdown: denominations,
              closedBy: userName,
            }),
          });
          console.log("✅ Turno guardado en BD como pending_authorization");
        } catch (err) {
          console.error("❌ Error guardando turno pendiente en BD:", err);
        }

        toast({
          title: "⚠️ Autorización requerida",
          description:
            "El turno requiere autorización de supervisor. No puedes cancelar.",
          variant: "destructive",
          duration: 8000,
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "No se pudo cerrar el turno",
          variant: "destructive",
        });
      }
    } finally {
      setIsClosing(false);
    }
  };

  // ============ NUEVA FUNCIÓN: Autorizar turno pendiente ============
  // ============ AUTORIZAR TURNO PENDIENTE (CORREGIDO) ============
  // {Esta sección llama directamente al endpoint de autorización en lugar de usar operaciones IndexedDB falsas}
  const handleAuthorizePendingShift = async () => {
    if (!authorizationCode) {
      toast({
        title: "Error",
        description: "Ingresa el código de autorización",
        variant: "destructive",
      });
      return;
    }

    setIsAuthorizing(true);

    try {
      // {RUTA LOCAL: El turno offline se cerró con discrepancia y no se ha sincronizado}
      if (pendingAuthShift?.isLocal) {
        console.log("🔐 [POS] Autorizando cierre local offline...");
        lastLocalAuthRef.current = Date.now(); // {Guardar timestamp para evitar re-verificación}
        try {
          await authorizeLocalClose(authorizationCode);
        } finally {
          // {Asegurar limpieza del modal incluso si authorizeLocalClose falla}
          setPendingAuthShift(null);
          setShowDenominationModal(false);
          setAuthorizationCode("");
          setShowAuthorizationField(false);
          setShiftSummary(null);
          setSelectedCloseType(null);
          setIsAuthorizing(false);
        }
        return;
      }

      const pendingData = JSON.parse(
        localStorage.getItem("pendingAuthShift") || "{}",
      );
      const shiftId = pendingData.shiftId || pendingAuthShift?.shiftId;

      console.log(
        `🔐 [POS] Autorizando turno ${shiftId} con código: ${authorizationCode}`,
      );

      // {LLAMADA DIRECTA AL ENDPOINT DE AUTORIZACIÓN DEL BACKEND}
      const response = await fetch(`/api/shifts/${shiftId}/authorize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          authorizationCode: authorizationCode,
          authorizedBy: realUser?.id || "sistema",
        }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Error al autorizar");

      console.log("✅ [POS] Autorización exitosa:", result);

      // Limpiar localStorage
      localStorage.removeItem("pendingAuthShift");
      clearPendingAuthShift();

      // === ELIMINAR TURNO DE INDEXEDDB (causa raíz del bug) ===
      if (shiftId) {
        try {
          await indexedDBService.deleteOfflineShift(shiftId);
          console.log("🗑️ [POS] Turno eliminado de IndexedDB:", shiftId);
        } catch (e) {
          console.warn("⚠️ [POS] No se pudo eliminar turno de IndexedDB:", e);
        }
      }

      // Limpiar estados del modal
      setPendingAuthShift(null);
      setShowDenominationModal(false);
      setAuthorizationCode("");
      setShowAuthorizationField(false);
      setShiftSummary(null);
      setSelectedCloseType(null);

      // === FORZAR LIMPIEZA INMEDIATA - MÉTODO DIRECTO ===
      // 1. Eliminar caché
      queryClient.removeQueries({ queryKey: ["currentShift"] });

      // 2. Establecer explícitamente null para TODAS las variantes de clave
      queryClient.setQueryData(["currentShift"], null);
      queryClient.setQueryData(["currentShift", realUser?.id], null);
      if (currentShift?.employeeId) {
        queryClient.setQueryData(
          ["currentShift", currentShift.employeeId],
          null,
        );
      }

      // 3. Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ["shopSales"] });
      queryClient.invalidateQueries({ queryKey: ["shopInventory"] });

      // 4. Forzar refetch inmediato
      await refetchShift();

      // 5. Esperar y verificar
      await new Promise((resolve) => setTimeout(resolve, 300));
      console.log("🔍 [POS] Post-autorización currentShift:", currentShift);

      // Descargar acta de cierre en PDF
      if (shiftId) {
        const pdfUrl = `/api/shifts/${shiftId}/pdf`;
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = `acta_cierre_${shiftId.substring(0, 8)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      toast({
        title: "✅ Turno autorizado",
        description:
          "El turno se ha cerrado exitosamente y ya puedes abrir otro.",
      });
    } catch (error: any) {
      // {CORRECCIÓN: Añadir ': any' para resolver error de tipo unknown}
      console.error("❌ Error autorizando turno:", error);
      toast({
        title: "❌ Error",
        description: error?.message || "No se pudo autorizar el turno",
        variant: "destructive",
      });
    } finally {
      setIsAuthorizing(false);
    }
  };
  // ============ FIN FUNCIÓN CORREGIDA ============
  // ============ RENDER ============
  if (!currentShop) return null;

  if (!realUser && !ownerInShopMode) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-64px)]">
        <Card className="w-[400px]">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error de configuración
            </h3>
            <p className="text-muted-foreground mb-4">
              No se pudo encontrar un usuario para gestionar la tienda.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col md:flex-row overflow-hidden bg-muted/20">
      {/* Indicador Offline */}
      <OfflineIndicator
        isOfflineMode={isOfflineMode}
        pendingSalesCount={pendingSalesCount}
        isSyncing={isSyncing}
        syncProgress={syncProgress}
        onSync={syncPendingSales}
      />

      {/* Grilla de Productos */}
      <ProductGrid
        filteredInventory={filteredInventory}
        products={products}
        search={search}
        minPrice={minPrice}
        maxPrice={maxPrice}
        isLoading={isLoading}
        inventoryLoading={inventoryLoading}
        totalPages={totalPages}
        currentPage={currentPage}
        totalItems={totalItems}
        getCurrencySymbol={getCurrencySymbol}
        onSearchChange={setSearch}
        onMinPriceChange={setMinPrice}
        onMaxPriceChange={setMaxPrice}
        onClearFilters={clearFilters}
        onRefresh={refreshInventory}
        onPageChange={setCurrentPage}
        onAddToCart={addToCart}
      />

      {/* Sidebar del Carrito - Desktop */}
      <div className="hidden md:flex w-full md:w-[400px] bg-background border-l shadow-xl flex-col z-20">
        <div className="p-4 border-b bg-muted/10">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-xl flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Orden Actual
            </h2>
            {cart.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-700"
                onClick={clearCart}
              >
                Vaciar
              </Button>
            )}
          </div>
        </div>
        <CartSidebar
          cart={cart}
          cartTotal={cartTotal}
          cartTotalWithSurcharge={cartTotalWithSurcharge}
          displayCartItems={
            fullInventory.length > 0 ? fullInventory : inventoryData?.data || []
          }
          products={products}
          customer={customer}
          customerPhone={customerPhone}
          customerSearching={customerSearching}
          inventoryLoading={inventoryLoading}
          canUseShifts={canUseShifts}
          currentShift={currentShift}
          isClosingShift={isClosingShift}
          createSaleMutation={createSaleMutation}
          getCurrencySymbol={getCurrencySymbol}
          setCustomerPhone={setCustomerPhone}
          handleCustomerSearch={handleCustomerSearch}
          clearCustomer={clearCustomer}
          onUpdateQuantity={updateQuantity}
          onUpdateSurcharge={updateSurcharge}
          onRemoveFromCart={removeFromCart}
          onClearCart={clearCart}
          onPrepareCloseShift={prepareCloseShift}
          onCheckout={handleCheckout}
        />
      </div>

      {/* Botón flotante - Móvil */}
      <div className="md:hidden fixed bottom-4 right-4 z-50">
        <Button
          onClick={() => setIsCartOpen(true)}
          className="h-14 w-14 rounded-full shadow-lg bg-primary relative"
          size="icon"
        >
          <ShoppingCart className="h-6 w-6" />
          {cart.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-destructive text-white text-xs rounded-full h-6 w-6 flex items-center justify-center">
              {cart.length}
            </span>
          )}
        </Button>

        {/* Modal del carrito - Móvil */}
        {isCartOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setIsCartOpen(false)}
          >
            <div
              className="absolute bottom-0 left-0 right-0 bg-background rounded-t-2xl flex flex-col"
              style={{ maxHeight: "90vh", height: "90vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b bg-background sticky top-0 z-10 shrink-0">
                <div className="flex justify-between items-center">
                  <h2 className="font-heading font-bold text-xl flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    Orden Actual
                  </h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsCartOpen(false)}
                  >
                    <span className="text-xl">✕</span>
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <CartSidebar
                  cart={cart}
                  cartTotal={cartTotal}
                  cartTotalWithSurcharge={cartTotalWithSurcharge}
                  displayCartItems={
                    fullInventory.length > 0
                      ? fullInventory
                      : inventoryData?.data || []
                  }
                  products={products}
                  customer={customer}
                  customerPhone={customerPhone}
                  customerSearching={customerSearching}
                  inventoryLoading={inventoryLoading}
                  canUseShifts={canUseShifts}
                  currentShift={currentShift}
                  isClosingShift={isClosingShift}
                  createSaleMutation={createSaleMutation}
                  getCurrencySymbol={getCurrencySymbol}
                  setCustomerPhone={setCustomerPhone}
                  handleCustomerSearch={handleCustomerSearch}
                  clearCustomer={clearCustomer}
                  onUpdateQuantity={updateQuantity}
                  onUpdateSurcharge={updateSurcharge}
                  onRemoveFromCart={removeFromCart}
                  onClearCart={clearCart}
                  onPrepareCloseShift={prepareCloseShift}
                  onCheckout={handleCheckout}
      />
          </div>
        </div>
          </div>
        )}
      </div>

      {/* Modales de Pago */}
      <PaymentModal
        show={showPaymentMethod}
        onSelectPayment={confirmPayment}
        onCancel={() => setShowPaymentMethod(false)}
      />

      <TransferFormModal
        show={showTransferForm}
        transferData={transferData}
        onUpdateField={(field, value) =>
          setTransferData({ ...transferData, [field]: value })
        }
        onConfirm={handleTransferConfirm}
        onCancel={() => {
          setShowTransferForm(false);
          setTransferData(DEFAULT_TRANSFER_DATA);
        }}
        bankCards={shopBankCards} // ← NUEVO: Pasar tarjetas cargadas
      />

      <CashInputModal
        show={showCashInput}
        cartTotal={cartTotal}
        getCurrencySymbol={getCurrencySymbol}
        onConfirm={(amount) => processSale("efectivo", amount)}
        onBack={() => {
          setShowCashInput(false);
          setShowPaymentMethod(true);
        }}
      />

      {/* {esta sección renderiza el nuevo modal de pago dividido} */}
      <SplitPaymentModal
        show={showSplitPayment}
        cartTotal={cartTotal}
        getCurrencySymbol={getCurrencySymbol}
        onConfirm={handleSplitPaymentConfirm}
        onCancel={() => {
          setShowSplitPayment(false);
          setShowPaymentMethod(true);
        }}
        bankCards={shopBankCards} // ← NUEVO: Pasar las tarjetas cargadas
      />

      {/* Modal de Apertura de Turno */}
      <OpenShiftModal
        show={showOpenShiftModal}
        cashRegisters={cashRegisters}
        isOpeningShift={isOpeningShift}
        canUseShifts={canUseShifts}
        getCurrencySymbol={getCurrencySymbol}
        onOpenShift={handleOpenShift}
        onCancel={() => setShowOpenShiftModal(false)}
      />

      {/* Modal de Tipo de Cierre */}
      <CloseTypeModal
        show={showCloseTypeModal}
        userName={userName}
        onSelect={handleSelectCloseType}
        onCancel={() => setShowCloseTypeModal(false)}
      />

      {/* Modal de Desglose de Denominaciones */}
      <DenominationModal
        show={showDenominationModal}
        userName={userName}
        denominations={denominations}
        tempClosingNotes={tempClosingNotes}
        showAuthorizationField={showAuthorizationField}
        authorizationCode={authorizationCode}
        isClosing={isClosing}
        isAuthorizing={isAuthorizing}
        pendingAuthShift={!!pendingAuthShift}
        getCurrencySymbol={getCurrencySymbol}
        calculateTotal={() => calculateTotalFromDenominations(denominations)}
        onDenominationChange={setDenominations}
        onNotesChange={setTempClosingNotes}
        onAuthorizationCodeChange={setAuthorizationCode}
        onConfirm={
          showAuthorizationField || pendingAuthShift
            ? handleAuthorizePendingShift
            : handleCloseShift
        }
        onCancel={() => {
          if (showAuthorizationField || pendingAuthShift) {
            toast({
              title: "⚠️ Autorización requerida",
              description: "No puedes cancelar",
              variant: "destructive",
            });
            return;
          }
          setShowDenominationModal(false);
          setDenominations(DEFAULT_DENOMINATIONS);
        }}
      />

    </div>
  );
}
